import React from 'react';

import { Close, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { Operation } from 'src/types/operation';

type ValidationSummaryModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  validation: {
    newFunds: Array<{
      operation: Operation;
      quotas: Array<{ class: string; series: string; amount: number }>;
    }>;
    existingFundUpdates: Array<{
      walletFundId: number;
      operation: Operation;
      newQuotas: Array<{ class: string; series: string; amount: number }>;
      updatedQuotas: Array<{
        existingIndex: number;
        class: string;
        series: string;
        amount: number;
      }>;
    }>;
    duplicates: Array<{
      cnpj: string;
      class: string;
      series: string;
      entries: Array<{ amount: number; lineNumber: number }>;
    }>;
    notFoundFunds: Array<{ cnpj: string; class: string; series: string }>;
  };
  notFoundCnpjs?: string[];
  nonConformingCnpjs?: string[];
  hasErrors: boolean;
};

export function ValidationSummaryModal({
  open,
  onClose,
  onConfirm,
  onCancel,
  validation,
  notFoundCnpjs = [],
  nonConformingCnpjs = [],
  hasErrors,
}: ValidationSummaryModalProps) {
  // Filtrar CNPJs não encontrados que também estão em não conformes
  // Se um CNPJ está em nonConforming, significa que foi encontrado mas não é FIDC
  // então não deve aparecer como "não encontrado"
  const filteredNotFoundFunds = validation.notFoundFunds.filter(
    (fund) => !nonConformingCnpjs.includes(fund.cnpj),
  );
  const filteredNotFoundCnpjs = notFoundCnpjs.filter(
    (cnpj) => !nonConformingCnpjs.includes(cnpj),
  );

  const hasNewFunds = validation.newFunds.length > 0;
  const hasUpdates = validation.existingFundUpdates.length > 0;
  const hasDuplicates = validation.duplicates.length > 0;
  const hasNotFound =
    filteredNotFoundFunds.length > 0 || filteredNotFoundCnpjs.length > 0;
  const hasNonConforming = nonConformingCnpjs.length > 0;

  const getTotalNewQuotas = () => {
    return validation.newFunds.reduce(
      (sum, fund) => sum + fund.quotas.length,
      0,
    );
  };

  const getTotalUpdatedQuotas = () => {
    return validation.existingFundUpdates.reduce(
      (sum, update) =>
        sum + update.newQuotas.length + update.updatedQuotas.length,
      0,
    );
  };

  const canProceed = !hasDuplicates;

  const noChangesToBeApplyOnWallet =
    !hasNewFunds &&
    !hasUpdates &&
    !hasDuplicates &&
    !hasNotFound &&
    !hasNonConforming;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
        Resumo da Importação
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" gutterBottom>
          Verifique o resumo das alterações que serão aplicadas à sua carteira:
        </Typography>

        <Box sx={{ mt: 2 }}>
          {hasNonConforming && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" color="error">
                  Fundos Não Conformes ({nonConformingCnpjs.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="error" sx={{ mb: 2 }}>
                  Estes fundos não são do mercado FIDC e não serão importados.
                </Alert>
                <List dense>
                  {nonConformingCnpjs.map((cnpj, index) => (
                    <ListItem key={index} divider>
                      <ListItemText primary={cnpj} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
          {hasNewFunds && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" color="primary">
                  Novos Fundos ({validation.newFunds.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {getTotalNewQuotas()} nova(s) cota(s) em{' '}
                  {validation.newFunds.length} fundo(s)
                </Typography>
                <List dense>
                  {validation.newFunds.map((fund, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={fund.operation.name}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              CNPJ: {fund.operation.cnpj}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Cotas:{' '}
                              {fund.quotas
                                .map(
                                  (q) =>
                                    `${q.class} Série ${q.series} (${q.amount})`,
                                )
                                .join(', ')}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {hasUpdates && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" color="warning.main">
                  Atualizações em Fundos Existentes (
                  {validation.existingFundUpdates.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {getTotalUpdatedQuotas()} atualização(ões) de cotas
                </Typography>
                <List dense>
                  {validation.existingFundUpdates.map((update, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={update.operation.name}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              CNPJ: {update.operation.cnpj}
                            </Typography>
                            {update.newQuotas.length > 0 && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="success.main">
                                Novas cotas:{' '}
                                {update.newQuotas
                                  .map(
                                    (q) =>
                                      `${q.class} Série ${q.series} (${q.amount})`,
                                  )
                                  .join(', ')}
                              </Typography>
                            )}
                            {update.updatedQuotas.length > 0 && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="warning.main">
                                Cotas atualizadas:{' '}
                                {update.updatedQuotas
                                  .map(
                                    (q) =>
                                      `${q.class} Série ${q.series} (${q.amount})`,
                                  )
                                  .join(', ')}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {hasDuplicates && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" color="error">
                  Registros Duplicados ({validation.duplicates.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Estes registros aparecem mais de uma vez no arquivo CSV.
                  Corrija-os antes de importar.
                </Alert>
                <List dense>
                  {validation.duplicates.map((dup, index) => (
                    <ListItem key={index} divider>
                      <ListItemText
                        primary={`${dup.cnpj} - ${dup.class} Série ${dup.series}`}
                        secondary={
                          <Typography variant="caption" color="error">
                            Linhas:{' '}
                            {dup.entries
                              .map((e) => `${e.lineNumber} (${e.amount})`)
                              .join(', ')}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {hasNotFound && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6" color="warning.main">
                  Fundos Não Encontrados (
                  {filteredNotFoundFunds.length + filteredNotFoundCnpjs.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Estes fundos não foram encontrados na base de dados e serão
                  ignorados. Verifique os CNPJs se necessário.
                </Alert>
                <List dense>
                  {filteredNotFoundFunds.map((fund, index) => (
                    <ListItem key={`validation-${index}`} divider>
                      <ListItemText
                        primary={fund.cnpj}
                        secondary={`${fund.class} Série ${fund.series}`}
                      />
                    </ListItem>
                  ))}
                  {filteredNotFoundCnpjs.map((cnpj, index) => (
                    <ListItem key={`api-${index}`} divider>
                      <ListItemText primary={cnpj} />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {noChangesToBeApplyOnWallet && (
            <Alert severity="info">
              Nenhuma alteração será aplicada à carteira.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} variant="outlined">
          Cancelar
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={!canProceed}>
          {!canProceed
            ? 'Corrija as duplicatas para continuar'
            : hasNotFound || hasNonConforming
              ? 'Importar (ignorando fundos inválidos)'
              : 'Importar alterações'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
